import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { parse } from 'node-html-parser';
import { HTMLElement } from 'node-html-parser';
import React, { useEffect } from 'react';
import styled from 'styled-components';
import validator from 'validator';

import Component404 from '../../components/_molecules/Component404';
import LandingPageLayout from '../../components/layouts/LandingPageLayout';

export default function CmsPage({
  htmlContent,
  pageTitle,
  coverImageUrl,
  pageDescription,
}: {
  htmlContent: string;
  pageTitle?: string;
  pageDescription?: string;
  coverImageUrl?: string;
}) {
  useEffect(() => {
    // This useEffect is solely for removing the webflow badge since the Webflow setting of removing the badge does not work
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        // If the mutation added nodes
        if (mutation.addedNodes) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          mutation.addedNodes.forEach((node: any) => {
            // If the node is an element and has the class 'w-webflow-badge' remove the element
            if (
              node.nodeType === Node.ELEMENT_NODE &&
              node.classList.contains('w-webflow-badge')
            ) {
              node.remove();
            }
          });
        }
      });
    });

    // Start observing the document with the configured parameters
    observer.observe(document, { childList: true, subtree: true });

    // Clean up
    return () => observer.disconnect();
  }, []);
  if (!htmlContent) {
    return (
      <LandingPageLayout hideBlobs={true} disableHeadline={true}>
        <Component404 />
      </LandingPageLayout>
    );
  }

  return (
    <Wrapper>
      <LandingPageLayout
        imageUrl={coverImageUrl}
        title={pageTitle}
        subTitle={pageDescription}
        hideBlobs={true}
        disableHeadline={true}
      >
        <ErrorBoundary>
          <Head>
            {/* This is a hack to make sure the CMS properties are not overwriting the overflow */}
            <style>
              {`
                html {
                  height: unset !important;
                }
              `}
            </style>
          </Head>
          <div dangerouslySetInnerHTML={{ __html: htmlContent }}></div>
        </ErrorBoundary>
      </LandingPageLayout>
    </Wrapper>
  );
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps> {
  componentDidCatch(error: Error, _: React.ErrorInfo) {
    // You could log the error or perform other actions here.
    console.error(error);
  }

  render() {
    return this.props.children;
  }
}

const Wrapper = styled.div``;

const getPageTitle = (html: HTMLElement): string | undefined => {
  try {
    const elements = html.getElementsByTagName('title');
    const firstTitle = elements[0];
    return firstTitle.firstChild?.rawText;
  } catch (e) {
    return undefined;
  }
};

const getPageDescription = (html: HTMLElement): string | undefined => {
  try {
    const elements = html.getElementsByTagName('p');
    const firstP = elements[0];
    return firstP.firstChild?.rawText;
  } catch (e) {
    return undefined;
  }
};

const getFirstImageUrl = (html: HTMLElement): string | undefined => {
  try {
    const elements = html.getElementsByTagName('img');
    const first = elements[0];
    return first.getAttribute('src');
  } catch (e) {
    return undefined;
  }
};

const rewriteBlogPostLinks = (html: string): string => {
  //Links on the blog to individual blog posts are not automatically prefixed with /c
  //We need to do this manually
  const root = parse(html);
  const links = root.querySelectorAll('a');

  links.forEach(link => {
    const href = link.getAttribute('href');
    if (href?.startsWith('/blog-posts/')) {
      link.setAttribute('href', `/c${href}`);
    }
  });

  return root.toString();
};

export const getServerSideProps: GetServerSideProps = async context => {
  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=300, stale-while-revalidate=3600',
  );
  const params = context.params as { slug: string[] }; // Forcing type based on the route

  const postSlug = params.slug.join('/');
  // some secure measure to make sure this is a string so we not get injections
  if (!validator.isSlug(postSlug)) {
    return { props: { htmlContent: null } };
  }
  const postSlugValidated = params.slug
    .map(segment => encodeURIComponent(segment))
    .join('/');

  const url = `https://curipod-cms.webflow.io/${postSlugValidated}`;
  const res = await fetch(url);

  const htmlContent =
    postSlug === 'blogs' ? rewriteBlogPostLinks(await res.text()) : await res.text();

  const parsedcontent = parse(htmlContent);
  const pageTitle = getPageTitle(parsedcontent);
  const coverImageUrl = getFirstImageUrl(parsedcontent) || null;
  const pageDescription = getPageDescription(parsedcontent) || '';

  // if we dont have the page the wordpress site will give back thair 404 page so we just check if the document contains that
  return { props: { htmlContent, pageTitle, coverImageUrl, pageDescription } };
};
